import { useState } from 'react';
import { T } from 'gt-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ArrowSquareOutIcon,
  CaretDownIcon,
  CaretRightIcon,
  FileTextIcon,
  StackIcon,
} from '@phosphor-icons/react';
import type {
  SupportingDocItem,
  SupportingDocSummary,
  BillOfLadingSummary,
  ProofOfDeliverySummary,
  ReceivingStampSummary,
  CarrierConfirmationSummary,
  ReceiptSummary,
} from '@erp/rpc';
import { useAPInvoiceSupportingDocuments } from '@/api/rpc/hooks/accounts-payable/useAPInvoices';
import { useDocumentOcrResultContent } from '@/api/rpc/hooks/core/useDocuments';
import { rpcClient } from '@/api/rpc/client';
import { getClassificationLabel } from '@/utils/emailClassificationUtils';
import { useToast } from '@/context/ToastContext';
import { cn } from '@/lib/utils';

interface SupportingDocumentsSectionProps {
  apInvoiceId: string;
}

/**
 * Renders the supporting documents (BOL, POD, Receiving Stamp, Carrier
 * Confirmation) associated to an AP invoice. Each card shows a structured
 * digest of the OCR extraction (BOL #, PO refs, dates, totals, etc.) plus an
 * expandable "Raw extraction" view that lazy-loads the full JSON.
 *
 * Self-hides when the invoice has no supporting documents, so it can be
 * dropped into the invoice details page without a guard at the call site.
 */
export function SupportingDocumentsSection({
  apInvoiceId,
}: SupportingDocumentsSectionProps) {
  const [open, setOpen] = useState(true);
  const { documents, isLoading, error } =
    useAPInvoiceSupportingDocuments(apInvoiceId);

  if (!isLoading && !error && documents.length === 0) {
    return null;
  }

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          {/*
            Radix's CollapsibleTrigger forwards onClick, aria-expanded,
            aria-controls, and data-state onto this CardHeader (asChild +
            Slot composition). What it does NOT forward — because it
            assumes a native <button> — is keyboard focusability and
            Space/Enter activation. Without these, keyboard-only users
            can't reach the toggle. Add them explicitly here.
          */}
          <CardHeader
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen((prev) => !prev);
              }
            }}
            className={cn(
              'pb-2 cursor-pointer hover:bg-muted/40 rounded-t-lg',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <StackIcon className="h-4 w-4" />
                <T>Supporting Documents</T>
                {documents.length > 0 && (
                  <Badge variant="secondary" className="text-[10px] h-4">
                    {documents.length}
                  </Badge>
                )}
              </CardTitle>
              {open ? (
                <CaretDownIcon className="h-4 w-4 text-muted-foreground" />
              ) : (
                <CaretRightIcon className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-2">
            {isLoading && (
              <div className="text-xs text-muted-foreground py-2">
                <T>Loading supporting documents…</T>
              </div>
            )}
            {error && (
              <div className="text-xs text-red-600 py-2">
                <T>Failed to load supporting documents.</T>
              </div>
            )}
            {!isLoading &&
              !error &&
              documents.map((doc) => (
                <SupportingDocumentCard key={doc.documentId} doc={doc} />
              ))}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

type OcrStatus = NonNullable<SupportingDocItem['ocr']>['status'];

const OCR_STATUS_BADGE: Record<
  OcrStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' }
> = {
  pending: { label: 'OCR pending', variant: 'secondary' },
  processing: { label: 'OCR running', variant: 'secondary' },
  completed: { label: 'OCR complete', variant: 'default' },
  failed: { label: 'OCR failed', variant: 'destructive' },
};

function SupportingDocumentCard({ doc }: { doc: SupportingDocItem }) {
  const [openRaw, setOpenRaw] = useState(false);
  const [openingDoc, setOpeningDoc] = useState(false);
  const { showToast } = useToast();

  async function handleOpenDocument() {
    if (openingDoc) return;
    setOpeningDoc(true);
    try {
      const preview = await rpcClient.core.documents.getPreview({
        documentId: doc.documentId,
      });
      window.open(preview.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showToast({
        title: 'Could not open document',
        message: err instanceof Error ? err.message : 'Unknown error',
        color: 'error',
        icon: 'x',
      });
    } finally {
      setOpeningDoc(false);
    }
  }

  const ocrStatus = doc.ocr?.status;
  const statusBadge = ocrStatus ? OCR_STATUS_BADGE[ocrStatus] : null;
  // Hide the badge in the "happy path" — OCR completed and we got a usable
  // summary. Surface it when something is off so reviewers notice.
  const showStatusBadge =
    statusBadge && (ocrStatus !== 'completed' || !doc.summary);

  return (
    <div className="rounded-md border bg-card">
      <div className="flex items-center gap-2 px-3 py-2 border-b">
        <FileTextIcon className="h-4 w-4 text-muted-foreground shrink-0" />
        <Badge variant="outline" className="text-[10px] h-4 shrink-0">
          {getClassificationLabel(doc.classificationType)}
        </Badge>
        <span
          className="text-sm font-medium truncate min-w-0"
          title={doc.originalFileName}
        >
          {doc.originalFileName}
        </span>
        {showStatusBadge && (
          <Badge
            variant={statusBadge.variant}
            className="text-[10px] h-4 shrink-0"
          >
            {statusBadge.label}
          </Badge>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-7 px-2 shrink-0"
          onClick={handleOpenDocument}
          disabled={openingDoc}
        >
          <ArrowSquareOutIcon className="h-3.5 w-3.5 mr-1" />
          {openingDoc ? (
            <T context="button label while document is opening">Opening…</T>
          ) : (
            <T context="button label to open a document">Open</T>
          )}
        </Button>
      </div>

      <div className="px-3 py-2 space-y-2">
        <SupportingDocumentSummaryView doc={doc} />

        {doc.ocr && (
          <Collapsible open={openRaw} onOpenChange={setOpenRaw}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className={cn(
                  'flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground',
                  'cursor-pointer',
                )}
              >
                {openRaw ? (
                  <CaretDownIcon className="h-3 w-3" />
                ) : (
                  <CaretRightIcon className="h-3 w-3" />
                )}
                {openRaw ? (
                  <T context="toggle label to hide OCR extraction data">
                    Hide raw extraction
                  </T>
                ) : (
                  <T context="toggle label to view OCR extraction data">
                    View raw extraction
                  </T>
                )}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              {/* Children unmount when closed — fetch is truly lazy. */}
              <RawExtractionView ocrResultId={doc.ocr.id} />
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </div>
  );
}

function SupportingDocumentSummaryView({ doc }: { doc: SupportingDocItem }) {
  const ocr = doc.ocr;

  if (!ocr) {
    return (
      <p className="text-xs text-muted-foreground">
        <T>OCR has not been run for this document yet.</T>
      </p>
    );
  }

  if (ocr.status === 'pending' || ocr.status === 'processing') {
    return (
      <p className="text-xs text-muted-foreground">
        <T>
          OCR is still in progress — extracted fields will appear here once it
          completes.
        </T>
      </p>
    );
  }

  if (ocr.status === 'failed') {
    return (
      <p className="text-xs text-red-600">
        <T context="OCR processing error prefix">OCR failed</T>
        {ocr.error ? `: ${ocr.error}` : '.'}
      </p>
    );
  }

  if (!doc.summary) {
    return (
      <p className="text-xs text-muted-foreground">
        <T>
          OCR completed but the extracted shape did not match the expected
          schema for this document type.
        </T>
      </p>
    );
  }

  return <SummaryFields summary={doc.summary} />;
}

function SummaryFields({ summary }: { summary: SupportingDocSummary }) {
  switch (summary.classificationType) {
    case 'bill_of_lading':
      return <BillOfLadingFields s={summary} />;
    case 'proof_of_delivery':
      return <ProofOfDeliveryFields s={summary} />;
    case 'receiving_stamp':
      return <ReceivingStampFields s={summary} />;
    case 'carrier_confirmation':
      return <CarrierConfirmationFields s={summary} />;
    case 'receipt':
      return <ReceiptFields s={summary} />;
  }
}

interface KvField {
  label: string;
  value: string | null;
}

function KvList({ fields }: { fields: KvField[] }) {
  const visible = fields.filter((f) => f.value !== null && f.value !== '');
  if (visible.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        <T>No structured fields extracted.</T>
      </p>
    );
  }
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs">
      {visible.map((f) => (
        <div key={f.label} className="contents">
          <dt className="text-muted-foreground">{f.label}</dt>
          <dd className="break-words">{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function joinList(values: string[]): string | null {
  return values.length > 0 ? values.join(', ') : null;
}

function formatNumber(value: number | null, suffix?: string): string | null {
  if (value === null) return null;
  return suffix ? `${value} ${suffix}` : String(value);
}

function BillOfLadingFields({ s }: { s: BillOfLadingSummary }) {
  return (
    <KvList
      fields={[
        { label: 'BOL #', value: s.bolNumber },
        { label: 'PRO #', value: s.proNumber },
        {
          label: 'Carrier',
          value: s.carrierScac
            ? `${s.carrierName ?? '?'} (${s.carrierScac})`
            : s.carrierName,
        },
        { label: 'Ship date', value: s.shipDate },
        { label: 'Delivery date', value: s.deliveryDate },
        { label: 'Freight terms', value: s.freightTerms },
        { label: 'Referenced POs', value: joinList(s.poNumbers) },
        { label: 'Referenced SOs', value: joinList(s.soNumbers) },
        { label: 'Total pieces', value: formatNumber(s.totalPieces) },
        { label: 'Total pallets', value: formatNumber(s.totalPallets) },
        {
          label: 'Total weight',
          value: formatNumber(s.totalWeight, s.totalWeightUom ?? undefined),
        },
        {
          label: 'Line items',
          value: s.lineItemCount > 0 ? String(s.lineItemCount) : null,
        },
      ]}
    />
  );
}

function ProofOfDeliveryFields({ s }: { s: ProofOfDeliverySummary }) {
  const signature =
    s.signaturePresent === true
      ? 'Yes'
      : s.signaturePresent === false
        ? 'No'
        : null;
  return (
    <KvList
      fields={[
        { label: 'BOL #', value: s.bolNumber },
        { label: 'PRO #', value: s.proNumber },
        { label: 'Carrier', value: s.carrierName },
        { label: 'Delivery date', value: s.deliveryDate },
        { label: 'Received by', value: s.receivedByName },
        { label: 'Signature on file', value: signature },
        { label: 'Referenced POs', value: joinList(s.poNumbers) },
        { label: 'Referenced SOs', value: joinList(s.soNumbers) },
        {
          label: 'Line items',
          value: s.lineItemCount > 0 ? String(s.lineItemCount) : null,
        },
        {
          label: 'Exceptions',
          value: s.exceptionCount > 0 ? String(s.exceptionCount) : null,
        },
      ]}
    />
  );
}

function ReceivingStampFields({ s }: { s: ReceivingStampSummary }) {
  return (
    <KvList
      fields={[
        { label: 'Stamped on', value: s.underlyingDocumentType },
        { label: 'PO #', value: s.poNumber },
        { label: 'Other POs', value: joinList(s.additionalPoNumbers) },
        { label: 'BOL #', value: s.bolNumber },
        { label: 'Packing slip #', value: s.packingSlipNumber },
        { label: 'Supplier', value: s.supplierName },
        { label: 'Received date', value: s.receivedDate },
        { label: 'Received by', value: s.receivedBy },
        {
          label: 'Line items',
          value: s.lineItemCount > 0 ? String(s.lineItemCount) : null,
        },
        {
          label: 'Exceptions',
          value: s.exceptionCount > 0 ? String(s.exceptionCount) : null,
        },
      ]}
    />
  );
}

function CarrierConfirmationFields({ s }: { s: CarrierConfirmationSummary }) {
  const rate =
    s.agreedRate !== null
      ? s.rateCurrency
        ? `${s.agreedRate} ${s.rateCurrency}`
        : String(s.agreedRate)
      : null;
  return (
    <KvList
      fields={[
        { label: 'Confirmation #', value: s.confirmationNumber },
        { label: 'BOL #', value: s.bolNumber },
        { label: 'PRO #', value: s.proNumber },
        {
          label: 'Carrier',
          value: s.carrierScac
            ? `${s.carrierName ?? '?'} (${s.carrierScac})`
            : s.carrierName,
        },
        { label: 'Carrier MC #', value: s.carrierMcNumber },
        { label: 'Broker', value: s.brokerName },
        { label: 'Equipment', value: s.equipmentType },
        { label: 'Agreed rate', value: rate },
        { label: 'Pickup', value: s.shipDate },
        { label: 'Delivery', value: s.deliveryDate },
        { label: 'Referenced POs', value: joinList(s.poNumbers) },
        { label: 'Referenced SOs', value: joinList(s.soNumbers) },
        {
          label: 'Rate breakdown lines',
          value:
            s.rateBreakdownItemCount > 0
              ? String(s.rateBreakdownItemCount)
              : null,
        },
      ]}
    />
  );
}

function formatReceiptTotal(amount: number, currency: string | null): string {
  if (!currency) return String(amount);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    // currency comes from OCR and may not be a valid ISO code
    return `${amount} ${currency}`;
  }
}

function ReceiptFields({ s }: { s: ReceiptSummary }) {
  const total =
    s.totalAmount !== null
      ? formatReceiptTotal(s.totalAmount, s.currency)
      : null;
  return (
    <KvList
      fields={[
        { label: 'Vendor', value: s.vendorName },
        { label: 'Receipt #', value: s.receiptNumber },
        { label: 'Date', value: s.receiptDate },
        { label: 'Total', value: total },
        { label: 'Referenced POs', value: joinList(s.poNumbers) },
        { label: 'Referenced invoices', value: joinList(s.invoiceNumbers) },
        { label: 'Other refs', value: joinList(s.referenceNumbers) },
        {
          label: 'Line items',
          value: s.lineItemCount > 0 ? String(s.lineItemCount) : null,
        },
      ]}
    />
  );
}

function RawExtractionView({ ocrResultId }: { ocrResultId: string }) {
  const { data, isLoading, error } = useDocumentOcrResultContent(ocrResultId);

  if (isLoading) {
    return (
      <div className="mt-2 text-xs text-muted-foreground">
        <T>Loading raw extraction…</T>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mt-2 text-xs text-red-600">
        <T>Failed to load raw extraction.</T>
      </div>
    );
  }

  return (
    <pre
      className={cn(
        'mt-2 max-h-72 overflow-auto rounded bg-muted/40 p-2',
        'text-[11px] leading-tight font-mono whitespace-pre-wrap break-words',
      )}
    >
      {JSON.stringify(data.fullResult, null, 2)}
    </pre>
  );
}
